import { useState, useEffect } from "react";
import FiberManualRecordIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import PauseIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import StopCircleIcon from "@mui/icons-material/StopCircleOutlined";
import {
  CONTACT_CONNECTED_STATUS,
  CONTACT_RECORDING_PAUSE,
  CONTACT_RECORDING_START,
  CONTACT_RECORDING_RESUME,
  CONTACT_RECORDING_STOP,
  VAR_INITIAL_CONTACT_ID,
  VAR_CONTACT_ID,
  SNACK_BARS_SEVERITY_ERROR,
} from "../../../constants";
import SbuxTooltip from "../../../components/SbuxTooltip";
import SbuxTranslate from "../../../components/SbuxTranslate";
import SbuxConfirmationDialog from "../../../components/SbuxConfirmationDialog";
import { IconButton } from "@mui/material";
import SbuxSnackbars from "../../../components/SbuxSnackbars";
import styles from "./styles";
import useCss from "../../../hooks/useCss";
import { useTheme } from "@mui/material/styles";

interface ScreenRecordingButtonsProps {
  handleRecordingButtonTrigger: (args: {
    action: string;
    contactId: string;
    initialContactId: string;
  }) => Promise<void>;
  ccpContactStatus: string | null;
  contactAttributes: Record<string, { value: string }> | null;
  contactData?: unknown;
  contactRecordingState: string | null;
  loading: boolean;
  error: string | null;
  hideErrorDialog: () => void;
  isStopRecordingFeatureImplementedInAWS: boolean;
}

const ScreenRecordingButtons = ({
  handleRecordingButtonTrigger,
  ccpContactStatus,
  contactAttributes,
  contactRecordingState,
  loading,
  error,
  hideErrorDialog,
  isStopRecordingFeatureImplementedInAWS,
}: ScreenRecordingButtonsProps) => {
  const classes = useCss(styles);
  const theme = useTheme();
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  useEffect(() => {
    if (error) {
      hideErrorDialog();
    }
  }, [error, hideErrorDialog]);

  const isStartOrPauseRecordingButtonDisabled = () => {
    return contactRecordingState === CONTACT_RECORDING_STOP || loading;
  };
  const isStopRecordingButtonDisabled = () => {
    return (
      contactRecordingState === null ||
      contactRecordingState === CONTACT_RECORDING_STOP ||
      loading
    );
  };

  // const getBackgroundColor = () => {
  //   return [CONTACT_RECORDING_START, CONTACT_RECORDING_RESUME].includes(
  //     contactRecordingState
  //   )
  //     ? COLOR_GOLD
  //     : COLOR_GREEN_ACCENT;
  // };

  const pauseContactRecordingAndThenStop = async (
    contactId: string,
    initialContactId: string,
  ) => {
    if (
      contactRecordingState === null ||
      [CONTACT_RECORDING_PAUSE, CONTACT_RECORDING_STOP].includes(
        contactRecordingState,
      )
    ) {
      return;
    }
    await handleRecordingButtonTrigger({
      action: CONTACT_RECORDING_PAUSE,
      contactId,
      initialContactId,
    });
  };

  const handleButtonClick = async (action: string) => {
    if (ccpContactStatus !== CONTACT_CONNECTED_STATUS) {
      return;
    }
    const initialContactId =
      contactAttributes?.[VAR_INITIAL_CONTACT_ID]?.value ?? "";
    const contactId = contactAttributes?.[VAR_CONTACT_ID]?.value ?? "";
    const allowedActions = getAllowedActions();
    if (!allowedActions.includes(action)) {
      return;
    }
    if (
      action === CONTACT_RECORDING_STOP &&
      !isStopRecordingFeatureImplementedInAWS
    ) {
      await pauseContactRecordingAndThenStop(contactId, initialContactId);
    }
    await handleRecordingButtonTrigger({ action, contactId, initialContactId });
  };

  const getAllowedActions = () => {
    let allowedActionsList: string[] = [];
    switch (contactRecordingState) {
      case CONTACT_RECORDING_START:
        allowedActionsList = [CONTACT_RECORDING_PAUSE, CONTACT_RECORDING_STOP];
        break;
      case CONTACT_RECORDING_PAUSE:
        allowedActionsList = [CONTACT_RECORDING_RESUME, CONTACT_RECORDING_STOP];
        break;
      case CONTACT_RECORDING_RESUME:
        allowedActionsList = [CONTACT_RECORDING_PAUSE, CONTACT_RECORDING_STOP];
        break;
      case CONTACT_RECORDING_STOP:
        allowedActionsList = [];
        break;
      default:
        allowedActionsList = [CONTACT_RECORDING_START, CONTACT_RECORDING_STOP];
        break;
    }
    return allowedActionsList;
  };

  const getAvailableActionForStartButton = () => {
    let action;
    switch (contactRecordingState) {
      case CONTACT_RECORDING_START:
        action = CONTACT_RECORDING_PAUSE;
        break;
      case CONTACT_RECORDING_PAUSE:
        action = CONTACT_RECORDING_RESUME;
        break;
      case CONTACT_RECORDING_RESUME:
        action = CONTACT_RECORDING_PAUSE;
        break;
      default:
        action = CONTACT_RECORDING_START;
        break;
    }
    return action;
  };

  const isStartOrPauseRecordingButtonLoading = () => {
    return (
      loading &&
      contactRecordingState !== null &&
      [
        CONTACT_RECORDING_START,
        CONTACT_RECORDING_PAUSE,
        CONTACT_RECORDING_RESUME,
      ].includes(contactRecordingState)
    );
  };

  const isStopRecordingButtonLoading = () => {
    return loading && contactRecordingState === CONTACT_RECORDING_STOP;
  };

  const toggleDialogOpen = () => {
    setShowConfirmationDialog((value) => !value);
  };

  const getTooltipForStartButton = () => {
    const action = getAvailableActionForStartButton();
    if (contactRecordingState === CONTACT_RECORDING_STOP) {
      return `Recording was stopped and cannot be resumed`;
    }
    return `Click to ${action} recording`;
  };

  const getStartPauseOrResumeIcon = () => {
    const isDisabled = isStartOrPauseRecordingButtonDisabled();
    if (
      contactRecordingState !== null &&
      [CONTACT_RECORDING_START, CONTACT_RECORDING_RESUME].includes(
        contactRecordingState,
      )
    ) {
      return (
        <PauseIcon
          sx={{
            color: isDisabled ? "secondary" : theme.palette.common.black,
            fontSize: 24,
          }}
        />
      );
    }
    return (
      <FiberManualRecordIcon
        sx={{
          color: isDisabled ? "secondary" : theme.palette.redAlert.main,
          fontSize: 24,
        }}
      />
    );
  };

  const renderButtons = () => {
    return (
      <>
        {error && (
          <SbuxSnackbars
            open={!!error}
            message={error}
            severity={SNACK_BARS_SEVERITY_ERROR}
            handleClose={hideErrorDialog}
          />
        )}
        <SbuxTooltip
          title={<SbuxTranslate>{getTooltipForStartButton()}</SbuxTranslate>}
          disableFocusListener
          disableTouchListener
        >
          <div>
            <IconButton
              id="screenRecordingStart"
              className={classes.button}
              onClick={() => {
                handleButtonClick(getAvailableActionForStartButton());
              }}
              disableFocusRipple
              disableRipple
              disabled={
                isStartOrPauseRecordingButtonDisabled() ||
                isStartOrPauseRecordingButtonLoading()
              }
            >
              {getStartPauseOrResumeIcon()}
            </IconButton>
          </div>
        </SbuxTooltip>
        <SbuxTooltip
          title={
            <SbuxTranslate>
              {contactRecordingState === CONTACT_RECORDING_STOP
                ? "Recording was stopped and cannot be resumed"
                : "Click to stop recording entirely"}
            </SbuxTranslate>
          }
        >
          <div>
            <IconButton
              id="screenRecordingStop"
              className={`${classes.button} ${classes.red}`}
              onClick={() => setShowConfirmationDialog(true)}
              disableFocusRipple
              disableRipple
              disabled={
                isStopRecordingButtonDisabled() ||
                isStopRecordingButtonLoading()
              }
            >
              <StopCircleIcon
                sx={{
                  color: isStopRecordingButtonDisabled()
                    ? "secondary"
                    : theme.palette.common.black,
                  fontSize: 24,
                }}
              />
            </IconButton>
          </div>
        </SbuxTooltip>
        <SbuxConfirmationDialog
          open={showConfirmationDialog}
          toggleDialogOpen={toggleDialogOpen}
          handleOkClick={() => {
            handleButtonClick(CONTACT_RECORDING_STOP);
            toggleDialogOpen();
          }}
          heading="Confirm Stop Recording"
          content="Are you sure you want to stop this recording? This recording cannot be restarted once it is stopped."
        />
      </>
    );
  };

  return ccpContactStatus === CONTACT_CONNECTED_STATUS ? renderButtons() : null;
};

export default ScreenRecordingButtons;
